import apiClient from './client';
import { AIProvider, MatchData } from '../types';
import {
  normalizeDate,
  validateSport,
  validateAIProvider,
  sanitizeParam,
  validateMatchTime
} from './validation';

// ===== AI / Prompt API =====

export const getPrompts = async (adminId?: number | null): Promise<Record<string, any>> => {
  const params = new URLSearchParams();
  if (adminId !== null && adminId !== undefined) {
    params.append('admin_id', adminId.toString());
  }
  const url = params.toString() ? `/api/ai/prompt?${params}` : '/api/ai/prompt';
  const response = await apiClient.get(url);
  return response.data;
};

export const getPromptByModel = async (
  sport: string,
  model: string
): Promise<{ sport: string; model: string; prompt: string }> => {
  const validatedSport = validateSport(sport);
  const validatedModel = validateAIProvider(model);

  const response = await apiClient.post(`/api/ai/prompt/${validatedSport}/match`, null, {
    params: { model: validatedModel }
  });
  return response.data;
};

export const createPrompt = async (
  sport: string,
  prompt: string
): Promise<{ status: string; sport: string; prompt: string }> => {
  const validatedSport = validateSport(sport);

  const response = await apiClient.post(`/api/ai/prompt/${validatedSport}`, null, {
    params: { prompt: prompt }  // Remove sanitizeParam for prompt text
  });
  return response.data;
};

export const updatePrompt = async (
  sport: string,
  prompt: string
): Promise<{ status: string; sport: string; prompt: string }> => {
  const validatedSport = validateSport(sport);

  const response = await apiClient.put(`/api/ai/prompt/${validatedSport}`, null, {
    params: { prompt: prompt }  // Remove sanitizeParam for prompt text
  });
  return response.data;
};

export const getAIProviders = async (): Promise<AIProvider[]> => {
  const response = await apiClient.get('/api/ai/provider');
  // API returns { status, providers, available } - extract the available array
  const data = response.data;
  if (data && Array.isArray(data.available)) {
    return data.available;
  }
  // Fallback to providers if available is not present
  if (data && Array.isArray(data.providers)) {
    return data.providers.map((p: any) => ({
      name: p.value,
      display_name: p.label,
      enabled: true
    }));
  }
  return [];
};

export const getMatchPredictions = async (
  sport: string,
  matchId: number | string,
  adminId?: number
): Promise<{
  sport: string;
  match_id: number | null;
  match_info: {
    home_team: string;
    away_team: string;
    match_date: string;
    match_time: string;
  };
  predictions: Array<{
    prediction_id: number;
    match_id: number;
    ai_model: string;
    report: string;
    version: number;
    created_at: string;
    prompt_template: string | null;
    prompt_version: number | null;
    admin_id: number | null;
    admin_name: string | null;
  }>;
}> => {
  // admin_id 파라미터 추가
  const params = adminId !== undefined ? `?admin_id=${adminId}` : '';
  const response = await apiClient.get(`/api/ai/predictions/${matchId}${params}`);
  return response.data;
};

export const generatePrediction = async (
  provider: string,
  matchData: MatchData,
  promptTemplate?: string
): Promise<{
  status: string;
  provider: string;
  prediction: string;
  prediction_id: number | null;
  notion_page_id: string | null;
  prompt_id: number | null;
  prompt_template: string | null;
  db_saved: boolean;
  match_id: number | null;
  notion_saved: boolean;
}> => {
  try {
    // Validate provider
    const validatedProvider = validateAIProvider(provider);

    // Validate and normalize match data
    const normalizedMatchData: MatchData = {
      ...matchData,
      sport: validateSport(matchData.sport),
      match_date: normalizeDate(matchData.match_date),
      match_time: validateMatchTime(matchData.match_time),
      home_team: sanitizeParam(matchData.home_team) || matchData.home_team,
      away_team: sanitizeParam(matchData.away_team) || matchData.away_team,
      venue: sanitizeParam(matchData.venue),
    };

    const response = await apiClient.post(`/api/ai/prediction/${validatedProvider}`, {
      // match_id removed - backend will auto-generate it
      match_data: normalizedMatchData,
      // ai_provider is taken from URL path parameter, not body
      prompt_template: promptTemplate
    });

    return response.data;
  } catch (error: any) {
    // Handle validation errors
    if (error.message?.includes('Invalid')) {
      throw new Error(`Validation error: ${error.message}`);
    }

    // Handle API errors with better messages
    if (error.response?.status === 400) {
      throw new Error(`Bad request: ${error.response.data.detail || 'Invalid parameters'}`);
    }

    if (error.response?.status === 503) {
      throw new Error('Service temporarily unavailable. Please try again later.');
    }

    // Re-throw other errors
    throw error;
  }
};

// Prompt API
export const getPrompt = async (
  sport: string
): Promise<{ status: string; sport: string; prompt: any }> => {
  const validatedSport = validateSport(sport);

  const response = await apiClient.get(`/api/ai/prompts/${validatedSport}`);
  return response.data;
};

export const savePrompt = async (
  sport: string,
  data: { prompt: string },
  targetAdminId?: number | null  // administrator가 다른 관리자용으로 저장할 때
): Promise<{ status: string; sport: string; prompt_id: number; admin_id: number; message: string }> => {
  const validatedSport = validateSport(sport);

  const body: { template: string; target_admin_id?: number } = {
    template: data.prompt
  };
  if (targetAdminId !== null && targetAdminId !== undefined) {
    body.target_admin_id = targetAdminId;
  }
  const response = await apiClient.post(`/api/ai/prompts/${validatedSport}`, body);
  return response.data;
};

// 날짜별 경기 일괄 예측 생성
export const generatePredictionsByDate = async (
  sport: string,
  date: string,
  aiProvider: string,
  adminId?: number,
  forceNew: boolean = false,
  notionAdminId?: number
): Promise<{
  status: string;
  ai_provider: string;
  admin_id: number;
  results: {
    total: number;
    success: number;
    failed: number;
    notion_saved: number;
    details: Array<{
      match_id: number;
      prediction_status: string;
      notion_status: string;
      prediction_id?: number;
      error?: string;
      notion_error?: string;
    }>;
  };
}> => {
  const body: { sport: string; date: string; ai_provider: string; admin_id?: number; force_new: boolean; notion_admin_id?: number } = {
    sport: validateSport(sport),
    date: normalizeDate(date),
    ai_provider: validateAIProvider(aiProvider),
    force_new: forceNew
  };
  if (adminId) body.admin_id = adminId;
  if (notionAdminId) body.notion_admin_id = notionAdminId;

  const response = await apiClient.post('/api/prediction/generate/by-date', body);
  return response.data;
};
